import { Component, Watch } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import BaseFormEdit from '../../models/base.form.edit';
import moment from 'moment';
import AlleleValidator from '../../services/allele.validator.js';
import axios from 'axios';
import { EventBus } from '../../event-bus';
import Swal from 'sweetalert2';
import FileHelper from "@/helpers/file.helper";
import Constants from '@/constants';

@Component({})
export default class BaseEdit extends BaseFormEdit {
  public doctorConclusionFile: File | null = null;
  public unrelatedSearchConfirmFile: File | null = null;
  public personalDataConfirmFile: File | null = null;
  public files: any = [];
  form = { firstName: '', middleName: '', lastName: '' } as any;

  get canSetPriority() {
    return (
        this.$auth.user().functions.find((t: any) => t.routeName === "SetPatientPriority") != null
    );
  }

  diagnose: any = null;
  get anotherDiagnose() { return {key: 'Another', value: this.$t('Другое') } };

  get isRelativePatient() { return this.form.type == Constants.RelativePatient };

  get getCreateDate() {
    return this.form.createDate
      ? moment(String(this.form.createDate)).format('DD.MM.YYYY')
      : '';
  }

  public async getFormDataWithFile() {
    try
    {    
      await FileHelper.validateModification(this.doctorConclusionFile);
      await FileHelper.validateModification(this.unrelatedSearchConfirmFile);
      await FileHelper.validateModification(this.personalDataConfirmFile);

      const formData = new FormData();
      formData.append('doctorConclusionFile', this.doctorConclusionFile as File);
      formData.append('unrelatedSearchConfirmFile', this
        .unrelatedSearchConfirmFile as File);
      formData.append('personalDataConfirmFile', this
        .personalDataConfirmFile as File);
      formData.append('json', JSON.stringify(this.form));
  
      return formData;
    }    
    catch (e)
    {      
      throw new Error(this.$t('Ошибка прикрелпения файла') + `: <b>${e.message}</b>. ` + this.$t('Файл изменился или к файлу отсуствует доступ'));
    }  
  }

  public async findPhenotype() {
    const validAllele = AlleleValidator.validate(this);

    if (this.form.gender && this.form.birthday && validAllele) {
      this.form.isFindPhenotype = true;
      this.getFormDataWithFile().then((formData) => {
        axios
        .post('/patients', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
          this.$router.push({
            path: '/donors',
            query: {
              patientId: response.data.id,
              ageFrom: 18,
              ageTo: 65,
              coincidence: 10,
              column: 'age',
              orderby: 'desc',
              externalId: '',
            } as any,
          });
        })
        .catch((error) => {
          EventBus.$emit('error', {
            msg: this.$t('В процессе сохранения произошла ошибка!'),
          });
        });
      })
      .catch((e) => {
        Swal(this.$t('Ошибка') as string, e.message, 'warning');
      });
    } else {
      Swal(
        this.$t('Предупреждение') as string,
        this.$t('Нельзя начать поиск фенотипа без указания `Даты рождения`, `Пола` и валидных HLA фенотипов') as string,
        'warning'
      );
    }
  }

  get common() {
    let result = '';

    if (this.form.number) {
      if(this.form.lastName || this.form.firstName || this.form.middleName)
      {
        result += `, ${this.form.lastName} ${this.form.firstName} ${this.form.middleName}`;
      }

      if (this.age) {
        result += `, ${ this.age }`;
      }

      if (this.form.gender) {
        let gender = this.form.genders.filter((item: { key: any; }) => item.key === this.form.gender);
        result += `, ${gender && gender.length && gender[0].value}`;
      }
      
      if (this.form.diagnosisId) {
        let diagnosis = this.form.diagnoses.filter((item: { key: any; }) => item.key === this.form.diagnosisId);
        result += `, ${diagnosis && diagnosis.length && diagnosis[0].value}`;
      } else if(this.form.diagnosis) {
        result += ', ' + this.form.diagnosis;
      }

      if (this.form.bloodType) {
        let bloodType = this.form.bloodTypes.filter((item: { key: any; }) => item.key === this.form.bloodType);
        result += `, ${bloodType && bloodType.length && bloodType[0].value}`;
      }

      if (this.form.rh) {
        let rh = this.form.rhs.filter((item: { key: any; }) => item.key === this.form.rh);
        result += `, ${rh && rh.length && rh[0].value}`;
      }

      result += this.form.weight ? `, ${this.form.weight} ${this.$t('кг')}` : '';
    }

    return result;
  }

  get age() {
    if (!this.form.birthday) {
      return null;
    }

    const age: number | null = this.calculateAge(new Date(this.form.birthday));

    if (age == 0) {
      const month: number | null = this.calculateMonth(
        new Date(this.form.birthday)
      );

      return this.getDeclension(month, this.$t('месяц') as string, this.$t('месяца') as string, this.$t('месяцев') as string);
    } else {
      return this.getDeclension(age, this.$t('год') as string, this.$t('года') as string, this.$t('лет') as string);
    }
  }

  public getDeclension(
    num: any,
    nominativ: string,
    genetiv: string,
    plural: string
  ): string | null {
    if (num == undefined || num == null || isNaN(num)) {
      return num;
    }

    const total = num;

    num = num % 100;
    if (num >= 11 && num <= 19) {
      return total + ' ' + plural;
    }

    let declension = plural;
    const i = num % 10;

    switch (i) {
      case 1:
        declension = nominativ;
        break;
      case 2:
      case 3:
      case 4:
        declension = genetiv;
        break;
    }

    return total + ' ' + declension;
  }

  get gender() {
    return (
      this.form.genders.find((x: any) => x.key === this.form.gender) || {
        value: '',
      }
    ).value;
  }

  get diagnosisName() {
    return (
      this.form.diagnoses.find((x: any) => x.key === this.form.diagnosisId) || {
        value: '',
      }
    ).value;
  }

  get bloodType() {
    return (
      this.form.bloodTypes.find((x: any) => x.key === this.form.bloodType) || {
        value: '',
      }
    ).value;
  }

  get rh() {
    const rh = (
      this.form.rhs.find((x: any) => x.key === this.form.rh) || {
        key: '',
      }
    ).key;
    if (rh) {
      return rh === 1 ? 'Rh+' : 'rh-';
    }
  }

  get getStatus() {
    return (
      this.form.statuses.find((x: any) => x.key === this.form.status) || {
        value: '',
      }
    ).value;
  }

  protected getStatusByName(statusName: String) {
    return this.form.statuses.find((x: any) => x.value === this.$t(statusName as string));
  }

  get currentStatus() {
    return this.form.statuses && this.form.status
      ? this.form.statuses.find((x: any) => x.key === this.form.status)
      : null;
  }

  public statusIn(arr: String[]) {
    return this.currentStatus && arr.map(v => this.$t(v as string)).indexOf(this.currentStatus.value) > -1;
  }

  get emailNotificationIcon() {
    return this.form.isEmailNotification ? 'toggle-on' : 'toggle-off';
  }

  get emailNotificationStyle() {
    return this.form.isEmailNotification
      ? { color: 'green' }
      : { color: 'black' };
  }

  protected getStatusByKey(key: Number) {
    return this.form.statuses.find((x: any) => x.key == key);
  }

  onRemoveFile(fileTypeName: any, nextFielInfo: any) {
    if(fileTypeName == this.$t('Заключение на поиск от врача')) {
      //@ts-ignore
      this.$refs.donorConclusionUploader.clearFiles();
      this.form.doctorConclusionFileInfo = nextFielInfo;      
    } else if (fileTypeName == this.$t('Согласие пациента на неродственный поиск') && 
               this.$refs.unrelatedSearchConfirmFile) {
      //@ts-ignore
      this.$refs.unrelatedSearchConfirmFile.clearFiles();
      this.form.unrelatedSearchConfirmFileInfo = nextFielInfo;
    } else if (fileTypeName == this.$t('Согласие пациента на обработку персональных данных')) {
      //@ts-ignore
      this.$refs.personalDataConfirmFile.clearFiles();
      this.form.personalDataConfirmFileInfo = nextFielInfo;
    }
  }

  onFilesUpdated(files: any) {
    this.files = files;
    
    let fileType = this.$t('Заключение на поиск от врача');
    //@ts-ignore
    this.$refs.donorConclusionUploader.clearFiles();
    this.form.doctorConclusionFileInfo = files.find((t: any) => t.fileTypeName == fileType);     

    if(this.$refs.unrelatedSearchConfirmFile) {
      fileType = this.$t('Согласие пациента на неродственный поиск');
      //@ts-ignore
      this.$refs.unrelatedSearchConfirmFile.clearFiles();
      this.form.unrelatedSearchConfirmFileInfo = files.find((t: any) => t.fileTypeName == fileType);
    }

    fileType = this.$t('Согласие пациента на обработку персональных данных');
    //@ts-ignore
    this.$refs.personalDataConfirmFile.clearFiles();
    this.form.personalDataConfirmFileInfo = files.find((t: any) => t.fileTypeName == fileType);    
  }

  get isTkmDateVisible() {
    return (this.statusIn(['Активация донора', 'ТКМ выполнена']) ||
            (this.form.donors && this.form.donors.find((x: any) => x.statusName === 'Сбор выполнен')))
  }

  get diagnosesOptions() {
    if (!this.form.diagnoses) {
      return [];      
    }

    var another = { group: '', groups: [this.anotherDiagnose] };
    var fromDictionary = { group: 'Из справочника', groups: this.form.diagnoses };

    return [fromDictionary, another];
  }

  onDiagnoseChange(val: any) { 
    this.diagnose = val;

    if(!val || val.key === 'Another')
    {
      this.form.diagnosisId = null;
      this.form.diagnosis = null;
    }
    else 
    {
      this.form.diagnosisId = val.key;
    }
  }

  @Watch('isDataLoaded')
  onIsDataLoadedChanged() {
    if (this.isDataLoaded ) {
      if(!this.form.diagnosisId && this.form.diagnosis)
      {
        this.diagnose = this.anotherDiagnose;
      } 

      if(this.form.diagnosisId)     
      {
        this.diagnose = this.form.diagnoses.find((d: any) => d.key == this.form.diagnosisId);
      }
    }
  }
}
